import { useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTestersHubEnabled } from '@/shared/config/useFeatures';
import type { HelpScreenDoc, HelpStatus, HelpTour as HelpTourDef } from './model';
import { renderHelpMarkdown } from './markdown/renderHelpMarkdown';
import { useHelpPanelStore } from './helpPanelStore';
import { useHelpHighlight } from './useHelpHighlight';
import { useHelpLayer, useHelpScreen, useMarkReviewed, useResolvedHelpRoute } from './useHelpScreen';
import { isOpenGap, mergeGaps, type GapView } from './gapView';
import { HelpGapCallout } from './HelpGapCallout';
import { HelpFields } from './HelpFields';
import { HelpTour } from './HelpTour';
import { HelpReportIssue } from './HelpReportIssue';
import { tabSectionAnchor } from './helpTabGroups';

const STATUS_BADGE: Record<HelpStatus, string> = {
  draft: 'bg-amber-100 text-amber-800',
  'sme-review': 'bg-phblue-100 text-phblue-800',
  published: 'bg-green-100 text-green-800',
};

/** Frames to wait for a cross-screen chip's target to mount after navigating (~1 s at 60 fps). */
const PENDING_FRAMES = 60;

/** `btn-ship` → local; `/orders/search#btn-search` → cross-screen. */
function parseRef(ref: string): { route: string | null; id: string } {
  const hash = ref.indexOf('#');
  if (ref.startsWith('/') && hash > 0) return { route: ref.slice(0, hash), id: ref.slice(hash + 1) };
  return { route: null, id: ref };
}

/**
 * Living help panel (ADR-0066), docked on the right and mounted ONCE in App (never on /login).
 * Opened by the `?` toolbar button, F1 and the sidebar Help Contents leaf (all through
 * {@link useHelpPanelStore}). Renders only while open.
 *
 * `showWindowIds` (John, 26 Oct): only admins see the PowerHouse window id in the header and
 * get it on Copy; everyone else sees and copies the route alone — no source names on screen.
 */
export function HelpPanel({ showWindowIds = false }: { showWindowIds?: boolean }) {
  const open = useHelpPanelStore((s) => s.open);
  if (!open) return null;
  return <HelpPanelOpen showWindowIds={showWindowIds} />;
}

function HelpPanelOpen({ showWindowIds }: { showWindowIds: boolean }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const pbWindow = useHelpPanelStore((s) => s.pbWindow);
  const closePanel = useHelpPanelStore((s) => s.closePanel);
  const pendingHighlight = useHelpPanelStore((s) => s.pendingHighlight);
  const setPendingHighlight = useHelpPanelStore((s) => s.setPendingHighlight);
  const { highlight, pulse, clear, isMounted } = useHelpHighlight();
  const hubEnabled = useTestersHubEnabled();

  // Parameterized screens (/items/master/ACC/X) show their catalog route's help.
  const helpRoute = useResolvedHelpRoute(pathname);
  const docQ = useHelpScreen(helpRoute ?? null);
  const layerQ = useHelpLayer(helpRoute ?? '', hubEnabled && helpRoute != null);
  const doc = docQ.data ?? null;
  const layer = layerQ.data;
  const internal = hubEnabled && (layer ? layer.internal : true);
  const gaps = useMemo(() => mergeGaps(layer, doc?.gaps), [layer, doc]);
  const openGaps = gaps.filter(isOpenGap).length;
  const loading = helpRoute === undefined || (helpRoute != null && docQ.isPending);
  // Q163: on a later sibling tab, the group's help opens scrolled to that tab's own section.
  const tabAnchor = doc ? tabSectionAnchor(pathname, helpRoute, doc.sections) : null;

  const [copied, setCopied] = useState(false);
  const [activeTour, setActiveTour] = useState<{ route: string; tour: HelpTourDef } | null>(null);
  const pendingRouteRef = useRef<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePanel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [closePanel]);

  /**
   * Same reservation as TestCardDrawer (slice-3722): the panel is `fixed` and portalled, so
   * without it the right-hand 400px of the content area would still render, look enabled and
   * swallow every click (VAL #3090, ROGER #3093). `#root` and the shell are ordinary flow
   * boxes and Tailwind's preflight makes every box `border-box`, so padding-right on `#root`
   * narrows the content and lets it reflow. Restored exactly as found on unmount.
   */
  useEffect(() => {
    const root = document.getElementById('root');
    if (!root) return;
    const prev = root.style.paddingRight;
    // Mirrors the aside's own `w-[400px] max-w-full`: never reserve more than the viewport.
    root.style.paddingRight = 'min(400px, 100%)';
    return () => {
      root.style.paddingRight = prev;
    };
  }, []);

  useEffect(() => {
    if (!tabAnchor) return;
    document.getElementById(`help-sec-${tabAnchor}`)?.scrollIntoView?.({ block: 'start' });
  }, [tabAnchor]);

  // Leaving the panel drops any ring it put up.
  useEffect(() => clear, [clear]);

  // A cross-screen chip navigated here: pulse its control once the new screen mounts it.
  useEffect(() => {
    const id = pendingHighlight;
    const wanted = pendingRouteRef.current;
    if (!id) return;
    if (wanted && wanted !== pathname && wanted !== helpRoute) return;
    let frame = 0;
    let tries = 0;
    const attempt = () => {
      if (isMounted(id)) {
        pulse(id);
        pendingRouteRef.current = null;
        setPendingHighlight(null);
        return;
      }
      tries += 1;
      if (tries > PENDING_FRAMES) {
        pendingRouteRef.current = null;
        setPendingHighlight(null);
        return;
      }
      frame = window.requestAnimationFrame(attempt);
    };
    frame = window.requestAnimationFrame(attempt);
    return () => window.cancelAnimationFrame(frame);
  }, [pathname, helpRoute, pendingHighlight, isMounted, pulse, setPendingHighlight]);

  const isLocal = (route: string | null) => route == null || route === pathname || route === helpRoute;

  const chipOf = (e: ReactMouseEvent) => (e.target as Element).closest?.('.help-chip') ?? null;

  const onContentOver = (e: ReactMouseEvent) => {
    const chip = chipOf(e);
    const ref = chip?.getAttribute('data-help-ref');
    if (!ref) return;
    const { route, id } = parseRef(ref);
    if (isLocal(route)) highlight(id);
  };

  const onContentOut = (e: ReactMouseEvent) => {
    const chip = chipOf(e);
    if (!chip) return;
    const to = e.relatedTarget as Node | null;
    if (to && chip.contains(to)) return;
    clear();
  };

  const onContentClick = (e: ReactMouseEvent) => {
    const el = e.target as Element;
    const chip = el.closest?.('.help-chip');
    if (chip) {
      const ref = chip.getAttribute('data-help-ref');
      if (!ref) return;
      e.preventDefault();
      const { route, id } = parseRef(ref);
      if (isLocal(route)) {
        pulse(id);
      } else if (route) {
        pendingRouteRef.current = route;
        setPendingHighlight(id);
        navigate(route);
      }
      return;
    }
    const link = el.closest?.('a.help-screen-link');
    if (link) {
      const href = link.getAttribute('href');
      if (href && href.startsWith('/')) {
        e.preventDefault();
        navigate(href);
      }
      return;
    }
    const marker = el.closest?.('.help-gap-marker');
    if (marker) {
      const gapId = marker.getAttribute('data-gap-id');
      if (gapId) document.getElementById(`help-gap-${gapId}`)?.scrollIntoView?.({ block: 'nearest' });
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(
        showWindowIds ? `${pbWindow ?? ''}  ${pathname}`.trim() : pathname,
      );
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      // Clipboard permission denied: silently ignore (uncommon path).
    }
  };

  const tour = activeTour && activeTour.route === pathname ? activeTour.tour : null;
  const authoredRoute = helpRoute ?? pathname;

  return createPortal(
    <aside
      className="fixed inset-y-0 right-0 z-40 flex w-[400px] max-w-full flex-col border-l border-slate-300 bg-white shadow-2xl"
      role="complementary"
      aria-label="Help"
      data-testid="help-panel"
    >
      <header className="flex items-start justify-between gap-2 border-b bg-slate-50 px-4 py-2.5">
        <div className="min-w-0">
          <h2 className="truncate text-sm font-semibold text-slate-900">{doc?.title ?? pathname}</h2>
          <p className="flex items-center gap-1.5 font-mono text-[11px] text-slate-500">
            <span className="truncate" data-testid="help-panel-ids">
              {showWindowIds ? `${doc?.pbWindow ?? pbWindow ?? '—'} · ${pathname}` : pathname}
            </span>
            <button
              type="button"
              onClick={() => void copy()}
              className="shrink-0 rounded border border-slate-300 bg-white px-1.5 font-sans text-[10px] text-slate-600 hover:bg-slate-50"
              title={
                showWindowIds
                  ? 'Copy the screen id and route (quote either when reporting an issue)'
                  : 'Copy the route (quote it when reporting an issue)'
              }
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
          </p>
          {hubEnabled && doc && (
            <div className="mt-1 flex items-center gap-1.5">
              <span
                className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${STATUS_BADGE[doc.status] ?? 'bg-slate-100 text-slate-700'}`}
                data-testid="help-status-badge"
              >
                {doc.status}
              </span>
              {internal && (
                <span
                  className={
                    'rounded-full px-1.5 py-0.5 text-[10px] font-medium ' +
                    (openGaps > 0 ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-600')
                  }
                  data-testid="help-gap-count"
                >
                  {openGaps} open {openGaps === 1 ? 'gap' : 'gaps'}
                </span>
              )}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={closePanel}
          className="shrink-0 rounded p-1 text-slate-400 hover:text-slate-700"
          aria-label="Close help"
          title="Close (Esc)"
        >
          ✕
        </button>
      </header>

      <div
        className="help-content min-h-0 flex-1 overflow-y-auto px-4 py-3 text-sm text-slate-800"
        data-testid="help-panel-content"
        onMouseOver={onContentOver}
        onMouseOut={onContentOut}
        onClick={onContentClick}
      >
        {loading ? (
          <p className="text-slate-500">Loading help…</p>
        ) : docQ.isError ? (
          <p className="text-slate-500">Help for this screen could not be loaded. Try again later.</p>
        ) : doc ? (
          <HelpBody
            doc={doc}
            gaps={internal ? gaps : []}
            route={authoredRoute}
            tabAnchor={tabAnchor}
            onStartTour={(t) => setActiveTour({ route: pathname, tour: t })}
          />
        ) : (
          <div data-testid="help-empty">
            <p className="text-slate-600">No help has been written for this screen yet.</p>
            {hubEnabled && (
              <p className="mt-2 text-xs text-slate-500">
                Add <code className="font-mono">help/screens{authoredRoute}.md</code> — see
                docs/help_project/content-authoring-guide.md
              </p>
            )}
            {internal &&
              gaps.map((g) => <HelpGapCallout key={g.gapId} gap={g} route={authoredRoute} />)}
          </div>
        )}
      </div>

      <footer className="space-y-2 border-t bg-slate-50 px-4 py-2.5">
        <HelpReportIssue pbWindow={pbWindow} />
        <div className="flex flex-wrap items-center gap-2 border-t pt-2">
          {internal && helpRoute && (
            <MarkReviewed route={helpRoute} openGaps={openGaps} reviewedBy={layer?.review ?? null} available={layer != null} />
          )}
          <button
            type="button"
            disabled
            className="ml-auto rounded border border-slate-200 px-2 py-0.5 text-[11px] text-slate-400"
            title="What's new in 9.0: coming in Wave 2"
          >
            What&apos;s new
          </button>
        </div>
      </footer>

      {tour && <HelpTour key={tour.id} tour={tour} onExit={() => setActiveTour(null)} />}
    </aside>,
    document.body,
  );
}

function MarkReviewed({
  route,
  openGaps,
  reviewedBy,
  available,
}: {
  route: string;
  openGaps: number;
  reviewedBy: { reviewedBy: string; reviewedAt: string } | null;
  available: boolean;
}) {
  const m = useMarkReviewed(route);
  const disabled = !available || openGaps > 0 || m.isPending;
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => m.mutate({})}
        disabled={disabled}
        title={openGaps > 0 ? 'Resolve every open gap first' : undefined}
        className="rounded border border-slate-300 bg-white px-2 py-0.5 text-[11px] text-slate-700 hover:border-phblue-400 hover:text-phblue-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {m.isPending ? 'Saving…' : 'Mark reviewed'}
      </button>
      {reviewedBy && (
        <span className="text-[10px] text-slate-500">
          Reviewed by {reviewedBy.reviewedBy} {reviewedBy.reviewedAt.slice(0, 10)}
        </span>
      )}
      {m.isError && <span className="text-[10px] text-red-700">{m.error.message}</span>}
    </div>
  );
}

/** Sections in file order; gaps under their level-2 section; fields list after the fields section. */
function HelpBody({
  doc,
  gaps,
  route,
  tabAnchor,
  onStartTour,
}: {
  doc: HelpScreenDoc;
  gaps: GapView[];
  route: string;
  /** Q163: anchor of the live sibling tab's section (scrolled to and marked), or null. */
  tabAnchor: string | null;
  onStartTour: (t: HelpTourDef) => void;
}) {
  const anchors = new Set(doc.sections.map((s) => s.anchor));
  const unplaced = gaps.filter((g) => !anchors.has(g.section));
  const hasFieldsSection = anchors.has('fields');
  return (
    <div className="space-y-3">
      {doc.sections.map((s) => {
        const Heading = s.level === 2 ? 'h3' : 'h4';
        return (
          <section
            key={s.anchor}
            id={`help-sec-${s.anchor}`}
            data-section={s.anchor}
            data-tab-section={s.tab}
            data-tab-active={s.anchor === tabAnchor ? 'true' : undefined}
            className={s.anchor === tabAnchor ? '-mx-2 rounded border-l-4 border-phblue-400 bg-phblue-50 px-2 py-1' : undefined}
          >
            <Heading
              className={
                s.level === 2
                  ? 'mb-1 text-sm font-semibold text-slate-900'
                  : 'mb-1 text-xs font-semibold uppercase tracking-wide text-slate-600'
              }
            >
              {s.heading}
            </Heading>
            {s.anchor === 'how-to' && doc.tours.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-1.5">
                {doc.tours.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => onStartTour(t)}
                    className="rounded border border-phblue-300 bg-phblue-50 px-2 py-0.5 text-[11px] text-phblue-800 hover:bg-phblue-100"
                  >
                    Start tour: {t.title}
                  </button>
                ))}
              </div>
            )}
            <div
              className="help-md space-y-2 text-sm leading-relaxed"
              // Sanitized by renderHelpMarkdown (marked + DOMPurify, ADR-0066, Lane B).
              dangerouslySetInnerHTML={{ __html: renderHelpMarkdown(s.markdown) }}
            />
            {s.anchor === 'fields' && <HelpFields fields={doc.fields} />}
            {gaps
              .filter((g) => g.section === s.anchor)
              .map((g) => (
                <HelpGapCallout key={g.gapId} gap={g} route={route} />
              ))}
          </section>
        );
      })}
      {!hasFieldsSection && Object.keys(doc.fields).length > 0 && (
        <section>
          <h3 className="mb-1 text-sm font-semibold text-slate-900">Fields</h3>
          <HelpFields fields={doc.fields} />
        </section>
      )}
      {unplaced.length > 0 && (
        <section>
          <h3 className="mb-1 text-sm font-semibold text-slate-900">Other open questions</h3>
          {unplaced.map((g) => (
            <HelpGapCallout key={g.gapId} gap={g} route={route} />
          ))}
        </section>
      )}
    </div>
  );
}
